import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  Tabs,
  DatePicker,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SwapOutlined } from '@ant-design/icons';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { TabPane } = Tabs;

const Inventario = () => {
  const { user } = useAuth();
  const [inventario, setInventario] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modales
  const [modalVisible, setModalVisible] = useState(false);
  const [movimientoModalVisible, setMovimientoModalVisible] = useState(false);

  const [editingItem, setEditingItem] = useState(null);

  const [form] = Form.useForm();
  const [movimientoForm] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('1');

  // Verificar si es admin principal (sin empresa_id)
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;

  useEffect(() => {
    fetchInventario();
    fetchPersonal();
    if (activeTab === '2') {
      fetchMovimientos();
    }
    if (isMainAdmin) {
      fetchEmpresas();
    }
  }, [isMainAdmin, activeTab]);

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error al cargar empresas');
    }
  };

  const fetchPersonal = async () => {
    try {
      const response = await api.get('/personal');
      setPersonal(response.data);
    } catch (error) {
      console.error('Error al cargar personal');
    }
  };

  const fetchInventario = async () => {
    setLoading(true);
    try {
      const response = await api.get('/inventario');
      setInventario(response.data);
    } catch (error) {
      message.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  };

  const fetchMovimientos = async () => {
    setLoading(true);
    try {
      const response = await api.post('/inventario/movimientos', {}, { params: {} });
      // NOTA: El endpoint GET se maneja con el ID 'movimientos' en el controlador
      // Pero axios.get('/inventario/movimientos') funcionará si la ruta está bien configurada
      // En el controlador: case 'GET': if ($id === 'movimientos') ...
      const res = await api.get('/inventario/movimientos');
      setMovimientos(res.data);
    } catch (error) {
      console.error('Error cargando movimientos', error);
      // message.error('Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingItem) {
        await api.put(`/inventario/${editingItem.id}`, values);
        message.success('Item actualizado exitosamente');
      } else {
        await api.post('/inventario', values);
        message.success('Item creado exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingItem(null);
      fetchInventario();
    } catch (error) {
      message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMovimientoSubmit = async (values) => {
    setSubmitting(true);
    try {
      await api.post('/inventario/movimientos', values);
      message.success('Movimiento registrado exitosamente');
      setMovimientoModalVisible(false);
      movimientoForm.resetFields();
      fetchInventario();
      if (activeTab === '2') fetchMovimientos();
    } catch (error) {
      message.error(error.response?.data?.message || 'Error al registrar movimiento');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      ...record,
      empresa_id: record.empresa_id || undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/inventario/${id}`);
      message.success('Item eliminado exitosamente');
      fetchInventario();
    } catch (error) {
      message.error(error.message);
    }
  };

  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria',
      key: 'categoria',
    },
    {
      title: 'Precio',
      dataIndex: 'precio',
      key: 'precio',
      render: (precio) => `S/ ${precio}`,
    },
    {
      title: 'Stock',
      dataIndex: 'stock',
      key: 'stock',
      render: (stock, record) => (
        <Tag color={stock <= record.stock_minimo ? 'red' : 'green'}>
          {stock}
        </Tag>
      ),
    },
    {
      title: 'Stock Mínimo',
      dataIndex: 'stock_minimo',
      key: 'stock_minimo',
    },
    ...(isMainAdmin ? [{
      title: 'Empresa',
      dataIndex: 'empresa_nombre',
      key: 'empresa_nombre',
      render: (text) => text || '-',
    }] : []),
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >

          </Button>
          <Popconfirm
            title="¿Eliminar item?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} size="small">

            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const columnsMovimientos = [
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      render: (text) => dayjs(text).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      render: (tipo) => (
        <Tag color={tipo === 'entrada' ? 'green' : 'red'}>
          {tipo.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Producto',
      dataIndex: 'producto_nombre',
      key: 'producto',
      render: (text, record) => `${record.producto_codigo} - ${text}`,
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
    },
    {
      title: 'Responsable / Conductor',
      dataIndex: 'personal_nombre',
      key: 'personal',
      render: (text) => text || '-',
    },
    {
      title: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
    },
    {
      title: 'Usuario Registro',
      dataIndex: 'usuario_nombre',
      key: 'usuario',
    }
  ];

  return (
    <div>
      <Card
        title="Gestión de Inventario"
        extra={
          <Space>
            <Button
              icon={<SwapOutlined />}
              onClick={() => {
                movimientoForm.resetFields();
                setMovimientoModalVisible(true);
              }}
            >
              Registrar Movimiento
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingItem(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              Nuevo Item
            </Button>
          </Space>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Stock Actual" key="1">
            <Table
              columns={columns}
              dataSource={inventario}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
          <TabPane tab="Historial de Movimientos" key="2">
            <Table
              columns={columnsMovimientos}
              dataSource={movimientos}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Modal Item */}
      <Modal
        title={editingItem ? 'Editar Item' : 'Nuevo Item'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingItem(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="codigo" label="Código" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="nombre" label="Nombre" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="categoria" label="Categoría">
            <Select>
              <Select.Option value="Repuestos">Repuestos</Select.Option>
              <Select.Option value="Lubricantes">Lubricantes</Select.Option>
              <Select.Option value="Herramientas">Herramientas</Select.Option>
              <Select.Option value="Otros">Otros</Select.Option>
            </Select>
          </Form.Item>
          <Space style={{ display: 'flex', marginBottom: 8 }} align="baseline">
            <Form.Item name="precio" label="Precio" rules={[{ required: true }]}>
              <InputNumber min={0} precision={2} style={{ width: 150 }} />
            </Form.Item>
            <Form.Item name="stock" label="Stock Inicial" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: 150 }} />
            </Form.Item>
            <Form.Item name="stock_minimo" label="Stock Mínimo" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: 150 }} />
            </Form.Item>
          </Space>
          {isMainAdmin && (
            <Form.Item name="empresa_id" label="Empresa">
              <Select allowClear>
                {empresas.map((e) => (
                  <Select.Option key={e.id} value={e.id}>{e.nombre}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              {editingItem ? 'Actualizar' : 'Crear'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Movimiento */}
      <Modal
        title="Registrar Movimiento de Inventario"
        open={movimientoModalVisible}
        onCancel={() => setMovimientoModalVisible(false)}
        footer={null}
      >
        <Form form={movimientoForm} layout="vertical" onFinish={handleMovimientoSubmit}>
          <Form.Item name="tipo" label="Tipo de Movimiento" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="entrada">Entrada (Compra/Devolución)</Select.Option>
              <Select.Option value="salida">Salida (Entrega/Uso)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="inventario_id" label="Producto" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {inventario.map(item => (
                <Select.Option key={item.id} value={item.id}>
                  {item.codigo} - {item.nombre} (Stock: {item.stock})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="cantidad" label="Cantidad" rules={[{ required: true }]}>
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.tipo !== currentValues.tipo}
          >
            {({ getFieldValue }) =>
              getFieldValue('tipo') === 'salida' ? (
                <Form.Item name="personal_id" label="Entregado a (Conductor/Personal)" rules={[{ required: true }]}>
                  <Select showSearch optionFilterProp="children">
                    {personal.map(p => (
                      <Select.Option key={p.id} value={p.id}>{p.nombre_completo}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item name="observaciones" label="Observaciones">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              Registrar Movimiento
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Inventario;

